import React, { Component } from "react";
import { getEmptyImage } from "react-dnd-html5-backend";
import { DragSource } from "react-dnd";
import PhotoFrameCropResizeHandlerContainer from "./photoFrameCropResizeHandler/PhotoFrameCropResizeHandlerContainer";
import { Image, Video } from "views/components";
import ResizeBox from "views/components/Editor/ResizeBox";
import { Vector } from "views/components/Editor/elements/vector/Vector";
import InnerImageRotateHandle from "views/components/Editor/rotateHandler/InnerImageRotateHandle";
import style from "./style.module.css";
import {
  calculatePhotoFrameDisplayDimensions,
  getImageDimensions
} from "views/components/Editor/CropLayer/photoFrameCropping/PhotoFrameCroppingPreviewCalculations";
import { isVideo } from "lib/isVideo";
import { waitForElement } from "lib/DOMNodeUtils";
import { rotatePoint } from "lib/geometry/rotation";

const PhotoFrameMaskingDraggableImageSource = {
  beginDrag(props, monitor, component) {
    props.startPhotoFrameCropMoving();
    return {
      domId: props.photoFrame.domId
    };
  },
  endDrag(props, monitor) {
    const differenceFromInitialOffset =
      monitor.getDifferenceFromInitialOffset();

    const rotatedOffset = rotatePoint(
      differenceFromInitialOffset.x,
      differenceFromInitialOffset.y,
      0,
      0,
      -props.vectorElement.angle
    );

    props.finishPhotoFrameCropMoving({
      domId: monitor.getItem().domId,
      differenceFromInitialOffset: {
        ...differenceFromInitialOffset,
        ...rotatedOffset
      }
    });
  }
};

function collect(connect, monitor) {
  return {
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDragging: monitor.isDragging()
  };
}

export class PhotoFrameMaskingDraggableImage extends Component {
  constructor(props) {
    super(props);

    this.calculatePhotoFrameDimension =
      this.calculatePhotoFrameDimension.bind(this);
    this.getImageWrapBaseStyle = this.getImageWrapBaseStyle.bind(this);
    this.getImageRotationStyle = this.getImageRotationStyle.bind(this);
    this.getImageDimensions = this.getImageDimensions.bind(this);
    this.wrapperRef = React.createRef();
    // Zero-size marker positioned at the image centre inside the rotating
    // wrap. The rotating wrap's transform-origin is also the image centre,
    // so this marker's screen position is invariant under the wrap's
    // rotation — exactly what InnerImageRotateHandle needs as a pivot.
    this.rotatePivotRef = React.createRef();

    this.state = {
      syncInterval: null
    };
  }

  componentDidMount() {
    const { connectDragPreview } = this.props;
    if (connectDragPreview) {
      connectDragPreview(getEmptyImage(), {
        captureDraggingState: true
      });
    }

    waitForElement("video", this.wrapperRef.current).then(sourceVideo => {
      this.setState({
        sourceVideo
      });
    });
  }

  componentWillUnmount() {
    if (this.state.syncInterval) {
      clearInterval(this.state.syncInterval);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      (this.state.sourceVideo && !prevState.sourceVideo) ||
      (this.state.sourceVideo &&
        !this.state.sourceVideo.isSameNode(prevState.sourceVideo))
    ) {
      // our sourceVideo just appeared or changed so we should add a listener to it for time updates
      const { sourceVideo } = this.state;
      sourceVideo.addEventListener("timeupdate", () => {
        const videos = [
          ...(this.wrapperRef.current?.querySelectorAll("video") || [])
        ];
        const sourceVideoSrc = sourceVideo.firstChild?.src || sourceVideo.src;
        videos.forEach(video => {
          const videoSrc = video.firstChild?.src || video.src;
          if (
            !video.isSameNode(sourceVideo) &&
            videoSrc === sourceVideoSrc &&
            video.currentTime !== sourceVideo.currentTime
          ) {
            video.currentTime = sourceVideo.currentTime;
          }
        });
      });
    }

    if (!this.state.sourceVideo?.isConnected) {
      // our source video was removed, look for a new one?
      waitForElement("video", this.wrapperRef.current).then(sourceVideo => {
        this.setState({
          sourceVideo
        });
      });
    }
  }

  cancelClick(event) {
    event.stopPropagation();
    event.preventDefault();
  }

  calculatePhotoFrameDimension() {
    const { vectorElement, photoFrame, zoom, placeholderDimensions } =
      this.props;

    const unmaskedWidth = vectorElement.srcWidth * vectorElement.scale;
    const unmaskedHeight = vectorElement.srcHeight * vectorElement.scale;
    const maskedRightDiff = unmaskedWidth - vectorElement.width;
    const maskedBottomDiff = unmaskedHeight - vectorElement.height;

    const unmaskedVector = {
      ...vectorElement,
      width: unmaskedWidth,
      height: unmaskedHeight,
      maskedWidth: vectorElement.width,
      maskedHeight: vectorElement.height,
      maskedRightDiff,
      maskedBottomDiff
    };

    return calculatePhotoFrameDisplayDimensions({
      photoFrame,
      vectorElement: unmaskedVector,
      zoom,
      placeholderDimensions
    });
  }

  getImageDimensions() {
    const { zoom, photoFrame, vectorElement, placeholderDimensions } =
      this.props;

    return getImageDimensions({
      zoom,
      photoFrame,
      vectorElement,
      placeholderDimensions
    });
  }

  getImageWrapBaseStyle() {
    const { isCropping } = this.props;
    const photoFrameDimensions = this.calculatePhotoFrameDimension();

    return {
      position: "absolute",
      top: photoFrameDimensions.top,
      left: photoFrameDimensions.left,
      height: photoFrameDimensions.height,
      width: photoFrameDimensions.width,
      zIndex: 4,
      opacity: isCropping ? 0 : 1
    };
  }

  getImageRotationStyle() {
    const { photoFrame } = this.props;
    const rotation = photoFrame.rotation || 0;
    const imageDimensions = this.getImageDimensions();
    const photoFrameDimensions = this.calculatePhotoFrameDimension();

    // The <Image> is positioned inside the wrap at (offsetLeft, offsetTop)
    // with size (wrap.width, wrap.height). So the image's own centre within
    // the wrap is at (offsetLeft + width/2, offsetTop + height/2). Setting
    // transform-origin to that point rotates around the image centre — the
    // same rotation centre used by ImageInstructionContent on the masked
    // view, so the full preview and the masked view stay in sync.
    const originX = imageDimensions.left + photoFrameDimensions.width / 2;
    const originY = imageDimensions.top + photoFrameDimensions.height / 2;

    return {
      width: "100%",
      height: "100%",
      position: "absolute",
      transform: `rotate(${rotation}deg) scaleX(${photoFrame.scaleX}) scaleY(${photoFrame.scaleY})`,
      transformOrigin: `${originX}px ${originY}px`
    };
  }

  getWrapperStyle() {
    const { pageOffset, vectorElement, isCropping, zoom } = this.props;

    return {
      position: "absolute",
      top: pageOffset.top + vectorElement.top * zoom,
      left: pageOffset.left + vectorElement.left * zoom,
      height: vectorElement.height * zoom,
      width: vectorElement.width * zoom,
      zIndex: 5,
      opacity: isCropping ? 0 : 1,
      transform: [
        `rotate(${vectorElement.angle}deg)`,
        `scaleX(${vectorElement.scaleX})`,
        `scaleY(${vectorElement.scaleY})`
      ].join(" ")
    };
  }

  render() {
    const { connectDragSource, vectorElement, photoFrame, zoom } = this.props;

    const photoFrameDimensions = this.calculatePhotoFrameDimension();

    const Handle = ({ ...props }) => {
      return (
        <PhotoFrameCropResizeHandlerContainer
          vectorElement={vectorElement}
          {...props}
          photoFrame={photoFrame}
        />
      );
    };

    const imageDimensions = this.getImageDimensions();

    const imageWrapBaseStyle = this.getImageWrapBaseStyle();
    const imageRotationStyle = this.getImageRotationStyle();

    // when the preview is image instructions we want to replace without removing unchanged ones
    const elementInstructions = [...vectorElement.imageInstructions];

    const updatedImageInstructions = elementInstructions.map(
      currentInstruction => {
        if (currentInstruction.domId === photoFrame.domId) {
          return photoFrame;
        }
        return currentInstruction;
      },
      {}
    );

    const offsetLeft = imageDimensions.left;
    const offsetTop = imageDimensions.top;

    const { onInnerImageRotationPreview, onInnerImageRotationCommit } =
      this.props;

    const rotationDeg = photoFrame.rotation || 0;

    // Mirror PhotoFrameBar.isButtonDisabled("angle") so the on-canvas
    // handle and the action-bar button gate at drag-start on identical
    // conditions. Two failure modes, two different responses:
    //   1. Element has the "angle" restriction — rotation is locked at
    //      the template level, so the handle is *removed entirely* below
    //      to avoid a phantom disabled affordance hovering over the
    //      crop UI.
    //   2. Frame is empty / still loading / still in preview state —
    //      rotation makes no sense yet, but it will once the user picks
    //      an image. Render the handle disabled so it animates in cleanly
    //      when the frame becomes valid.
    // The disabledRef bridge inside InnerImageRotateHandle is
    // defence-in-depth: none of these inputs realistically flip false→true
    // mid-drag today (template restrictions are static, photoFrame.id is
    // stable, isPreview only goes true→false on upload completion).
    const restrictions = vectorElement?.restrictions ?? [];
    const isInnerImageRotateRestricted = restrictions.includes("angle");
    const innerImageRotateDisabled =
      isInnerImageRotateRestricted ||
      !(
        photoFrame &&
        photoFrame.id &&
        (photoFrame.mediaId || photoFrame.src) &&
        !photoFrame.isPreview
      );

    return connectDragSource(
      <div
        style={this.getWrapperStyle()}
        ref={this.wrapperRef}
        id={`${vectorElement.id}-draggable-image`}
      >
        {/* Full Image For Cropping */}
        <div onClick={this.cancelClick} style={imageWrapBaseStyle}>
          <div style={imageRotationStyle}>
            {isVideo(photoFrame.previewSrc) ? (
              <Video
                filter="brightness(0.5)"
                src={photoFrame.previewSrc}
                height={photoFrameDimensions.height}
                width={photoFrameDimensions.width}
                left={offsetLeft}
                top={offsetTop}
                isLandscape={true}
                className={style.image}
                autoPlay
                noBorders
              />
            ) : (
              <Image
                filter="brightness(0.5)"
                height={photoFrameDimensions.height}
                src={photoFrame.previewSrc}
                left={offsetLeft}
                top={offsetTop}
                width={photoFrameDimensions.width}
                isLandscape={true}
                className={style.image}
                noBorders
              />
            )}
          </div>
        </div>
        {/* Vector Frame - contains current cropped image */}
        <div
          style={{
            position: "absolute",
            transform: `scale(${zoom}) `,
            transformOrigin: "0 0",
            zIndex: 11
          }}
        >
          <Vector
            elementData={{
              ...vectorElement,
              imageInstructions: updatedImageInstructions
            }}
            zoom={zoom}
            autoPlay
            isCropMask
          />
        </div>
        {/* Rotate handle — rendered outside the image stacking context so the
            masked Vector frame (zIndex 11) cannot cover it. Rotates with the
            image so it orbits the bounds; counter-rotates its icon to stay
            upright. Skipped entirely when the element has the "angle"
            restriction so a locked frame doesn't show a phantom affordance. */}
        {onInnerImageRotationPreview &&
          onInnerImageRotationCommit &&
          !isInnerImageRotateRestricted && (
            // The pivot marker is a zero-size element positioned at the image
            // centre in wrap-local coords (the same point as transform-origin),
            // so its screen position stays put under the wrap's rotation. The
            // handle uses its bbox as the rotation pivot — robust against the
            // image sitting at non-zero (offsetLeft, offsetTop) inside the
            // wrap, which would otherwise drift the wrap's AABB centre away
            // from the image centre by R_θ(-offsetLeft, -offsetTop).
            <div
              style={{
                ...imageWrapBaseStyle,
                transform: `rotate(${rotationDeg}deg) scaleX(${photoFrame.scaleX}) scaleY(${photoFrame.scaleY})`,
                transformOrigin: `${
                  offsetLeft + photoFrameDimensions.width / 2
                }px ${offsetTop + photoFrameDimensions.height / 2}px`,
                zIndex: 20,
                pointerEvents: "none"
              }}
            >
              <div
                ref={this.rotatePivotRef}
                style={{
                  position: "absolute",
                  left: `${offsetLeft + photoFrameDimensions.width / 2}px`,
                  top: `${offsetTop + photoFrameDimensions.height / 2}px`,
                  width: 0,
                  height: 0
                }}
              />
              <InnerImageRotateHandle
                anchorLeft={offsetLeft + photoFrameDimensions.width / 2}
                anchorTop={offsetTop + photoFrameDimensions.height}
                counterRotateDeg={rotationDeg}
                counterScaleX={photoFrame.scaleX}
                counterScaleY={photoFrame.scaleY}
                pivotRef={this.rotatePivotRef}
                rotation={rotationDeg}
                disabled={innerImageRotateDisabled}
                onRotationPreview={onInnerImageRotationPreview}
                onRotationCommit={onInnerImageRotationCommit}
              />
            </div>
          )}
        {/* Resize Box — rotates with the image so the corner handles track
            the image's current bounds. Same transform origin as the image. */}
        <div
          style={{
            ...imageWrapBaseStyle,
            transform: `rotate(${rotationDeg}deg) scaleX(${photoFrame.scaleX}) scaleY(${photoFrame.scaleY})`,
            transformOrigin: `${
              offsetLeft + photoFrameDimensions.width / 2
            }px ${offsetTop + photoFrameDimensions.height / 2}px`,
            zIndex: 12
          }}
        >
          <div
            style={{
              top: offsetTop,
              left: offsetLeft,
              width: "100%",
              height: "100%",
              position: "absolute"
            }}
          >
            <ResizeBox
              Handler={Handle}
              left={0}
              top={0}
              isResizing={false}
              fixedZIndex={12}
              isCropping={true}
            />
          </div>
        </div>
      </div>
    );
  }
}

// Export the wrapped component:
export default DragSource(
  "PHOTO_FRAME_IMAGE",
  PhotoFrameMaskingDraggableImageSource,
  collect
)(PhotoFrameMaskingDraggableImage);
